import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Chip,
} from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';
import api from '../api';
import { showSuccess, showError } from '../utils/sweetAlertConfig';

const PRIMARY = "#29204C";

export default function CreateTechnicalTaskModal({ open, onClose, onSuccess, jobs }) {
  const [formData, setFormData] = useState({
    job_id: '',
    title: '',
    description: '',
    requirements: '',
    evaluation_criteria: '',
    deadline_days: 7,
    max_file_size: 10,
    max_files: 5,
    max_score: 100,
    passing_score: 60,
    allowed_file_types: ['pdf', 'zip', 'docx', 'txt'],
    status: 'draft',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.job_id) {
      showError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/technical-tasks', formData);
      showSuccess('Technical task created successfully!');
      handleClose();
      onSuccess();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      job_id: '',
      title: '',
      description: '',
      requirements: '',
      evaluation_criteria: '',
      deadline_days: 7,
      max_file_size: 10,
      max_files: 5,
      max_score: 100,
      passing_score: 60,
      allowed_file_types: ['pdf', 'zip', 'docx', 'txt'],
      status: 'draft',
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CodeIcon sx={{ color: PRIMARY }} />
          <Typography variant="h6">Create Technical Task</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Job Position</InputLabel>
              <Select
                name="job_id"
                value={formData.job_id}
                onChange={handleChange}
                label="Job Position"
              >
                {Array.isArray(jobs) && jobs.map(job => (
                  <MenuItem key={job.job_id} value={job.job_id}>
                    {job.posting_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Task Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              required
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Requirements"
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              placeholder="List the technical requirements and deliverables..."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Evaluation Criteria"
              name="evaluation_criteria"
              value={formData.evaluation_criteria}
              onChange={handleChange}
              placeholder="How will this task be evaluated..."
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Deadline (days)"
              name="deadline_days"
              value={formData.deadline_days}
              onChange={handleChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Max File Size (MB)"
              name="max_file_size"
              value={formData.max_file_size}
              onChange={handleChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Max Files"
              name="max_files"
              value={formData.max_files}
              onChange={handleChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Max Score"
              name="max_score"
              value={formData.max_score}
              onChange={handleChange}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Passing Score"
              name="passing_score"
              value={formData.passing_score}
              onChange={handleChange}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Allowed File Types:
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {formData.allowed_file_types.map(type => (
                <Chip key={type} label={type.toUpperCase()} size="small" />
              ))}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#1a1635' } }}
        >
          Create Task
        </Button>
      </DialogActions>
    </Dialog>
  );
}
